/**
 * Embedded Organization Context
 * Provides organization ID AND user role for embedded routes (Bitrix24 iframe)
 * Simplified version without full OrganizationContext complexity
 *
 * Authentication Flow:
 * 1. Bitrix24 opens /api/v1/embedded/app with AUTH_ID
 * 2. API verifies AUTH_ID and creates session in embedded_sessions
 * 3. API redirects with a one-time bootstrap token in the URL fragment
 * 4. This context exchanges it for an HttpOnly cookie and loads org/user data
 */
import {
  createContext,
  useCallback,
  useContext,
  useState,
  useEffect,
  useMemo,
  useRef,
  ReactNode,
} from 'react';
import { logError, logInfo, logWarn } from '@/lib/logger';
import {
  awaitEmbeddedRecoveryOutcome,
  notifyEmbeddedSessionExpired,
} from '@/lib/embedded-session-recovery';
import { type GlobalRole } from '@aica/core/rbac';
import { isWeekStartsOn, type WeekStartsOn } from '@aica/core';
import {
  embeddedBootstrapResponseSchema,
  embeddedSessionResponseSchema,
  type EmbeddedSessionResponse,
} from '@aica/api-types/schemas';
import { SessionFeatureFlagsProvider } from '@/contexts/FeatureFlagsContext';
import { takeEmbeddedBootstrapToken } from '@/lib/embedded-session';
import {
  embeddedHostDisplayName,
  embeddedCrmOrigin,
  embeddedHostHint,
  embeddedSessionAuthHeaders,
  frameUsesHostMessages,
  observeHostHeight,
  setEmbeddedSessionCredential,
} from '@/lib/embedded-host';
import { useTrialEndRefresh } from '@/hooks/useTrialEndRefresh';
import { useTrialStartRefreshOnFocus } from '@/hooks/useTrialStartRefreshOnFocus';

interface EmbeddedOrgContextValue {
  orgId: string | null;
  domain: string | null;
  /**
   * The CRM the frame lives in (`crmType` of the session; before the session —
   * the `host=` of the frame address), `null` when nothing names one. Screens
   * ask `embeddedHostUsesMessages(crmVendor)` (`@/lib/embedded-host`), not the
   * name. Optional so a stand-in context without it reads as Bitrix24.
   */
  crmVendor?: string | null;
  role: GlobalRole | null;
  userEmail: string | null;
  csrfToken: string | null;
  /** The session released the assistant for this portal (`features.assistant`, #765 §5.3). */
  assistantEnabled: boolean;
  /** The session released «Голос клиента» for this portal (`features.voiceOfCustomer`, #848). */
  voiceOfCustomerEnabled: boolean;
  /** The session could not ask that gate — not a "no" (`features.voiceOfCustomerUnchecked`). */
  voiceOfCustomerUnchecked: boolean;
  consentRequired: boolean | null;
  pendingConsent: boolean;
  /**
   * Whether the embedded onboarding survey (industry/role/team) is already done.
   * `false` only for a fresh install awaiting personalization → the survey gate
   * shows. `true`/`null` → dashboard renders. Defaults to `true` outside the
   * embedded provider so the standalone app never gates on it.
   */
  onboardingSurveyCompleted: boolean | null;
  /**
   * Подтверждён ли compliance организации. `false` → EmbeddedComplianceGate
   * показывает форму владельцу. Вне embedded-провайдера дефолт `true`, чтобы
   * standalone-приложение никогда не гейтилось на этом флаге.
   */
  complianceConfirmed: boolean | null;
  /**
   * The organization's first day of the week (#864), from the session —
   * every member's dashboard cuts its Week by it. `null` until the session
   * says (a pre-consent session never does).
   */
  weekStartsOn: WeekStartsOn | null;
  isLoading: boolean;
  error: string | null;
  clearConsentRequired: () => void;
  /**
   * Re-read the validated session from the backend. Used after consent grant so
   * the freshly-assigned org role (client for the first user) replaces the stale
   * pre-consent value — otherwise the org owner is wrongly gated behind
   * "Request Access".
   */
  refreshSession: (options?: RefreshSessionOptions) => Promise<SessionRole | void>;
}

/**
 * `quiet` re-reads the session WITHOUT the loading state and without turning a
 * failure into the page's error: the gates above the iframe show a spinner on
 * `isLoading`, and a background re-read (the access revision grew) must not
 * unmount the open screen. Failure is logged and leaves the session as it was.
 */
export interface RefreshSessionOptions {
  quiet?: boolean;
}

/**
 * The role the re-read session reported: `null` — none, `undefined` — the read
 * failed and the session still holds what it held. (The context's type also
 * allows `void`: a stand-in that reports nothing is the same as a failed read.)
 */
export type SessionRole = GlobalRole | null | undefined;

const EmbeddedOrgContext = createContext<EmbeddedOrgContextValue | null>(null);

/**
 * What the session released for this portal, as one value: the shell's
 * verdicts (`features`), the flag subset (`flags`, #1347) and the tier
 * verdicts (`gates`, #1897) and the screen flags the org has off
 * (`screenFlagsOff`, #1859) and the Pro trial to show (`proTrial`, #2816)
 * that `SessionFeatureFlagsProvider` turns into the
 * flag context. `null` until the
 * session is read — every feature absent, every flag off.
 */
type SessionAccess = Pick<
  EmbeddedSessionResponse,
  'orgId' | 'features' | 'flags' | 'gates' | 'screenFlagsOff' | 'proTrial'
>;

export function EmbeddedOrgProvider({ children }: { children: ReactNode }) {
  const [orgId, setOrgId] = useState<string | null>(null);
  const [domain, setDomain] = useState<string | null>(null);
  const [crmVendor, setCrmVendor] = useState<string | null>(() => embeddedHostHint());
  const [role, setRole] = useState<GlobalRole | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [csrfToken, setCsrfToken] = useState<string | null>(null);
  const [access, setAccess] = useState<SessionAccess | null>(null);
  const [consentRequired, setConsentRequired] = useState<boolean | null>(null);
  const [pendingConsent, setPendingConsent] = useState(false);
  const [onboardingSurveyCompleted, setOnboardingSurveyCompleted] = useState<
    boolean | null
  >(null);
  const [complianceConfirmed, setComplianceConfirmed] = useState<boolean | null>(null);
  const [weekStartsOn, setWeekStartsOn] = useState<WeekStartsOn | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // The bearer exchange named the CRM page to talk to (`crmOrigin`): the frame
  // reports its height to it from now on (see the effect below).
  const [pageReachable, setPageReachable] = useState(false);

  const clearConsentRequired = useCallback(() => setConsentRequired(false), []);
  const bootstrapTokenRef = useRef<string | null | undefined>(undefined);
  const bootstrapExchangeRef = useRef<Promise<void> | null>(null);
  // Session reads overlap (two growths of the access revision before the first
  // quiet read returned; a consent grant over a background one) and may land in
  // any order. Each read takes the next number when it STARTS; the session
  // state is committed only by a read newer than the last one committed, so an
  // older response landing last never rolls the context back to an older role.
  // A superseded read answers its caller with `sessionCommittedRole` — the role
  // the context now holds — never its own: a caller that waits for the rendered
  // role to equal the answer (`useAccessMe`) would wait forever for the old one.
  const sessionReadSeq = useRef(0);
  const sessionCommittedSeq = useRef(0);
  const sessionCommittedRole = useRef<SessionRole>(undefined);

  const ensureSessionCookie = useCallback(async () => {
    if (bootstrapTokenRef.current === undefined) {
      bootstrapTokenRef.current = takeEmbeddedBootstrapToken();
    }
    const token = bootstrapTokenRef.current;
    if (!token) return;

    if (!bootstrapExchangeRef.current) {
      bootstrapExchangeRef.current = (async () => {
        const { API_BASE_URL } = await import('@/lib/api/config');
        const response = await fetch(
          `${API_BASE_URL}/api/v1/embedded/session/bootstrap`,
          {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ bootstrapToken: token }),
          }
        );
        if (!response.ok) {
          throw new Error(
            `Session bootstrap expired. Please reopen via ${embeddedHostDisplayName(embeddedHostHint())}.`
          );
        }
        // A bearer host (Uspacy) gets the session id in the answer instead of
        // a cookie, and the origin its messages may go to (spec
        // 2026-10-29-uspacy-card-tab-session.md §5). Bitrix24's answer carries
        // neither and leaves nothing to keep; an answer that does not read is
        // treated the same — the session request after it then fails closed.
        let body: unknown = null;
        try {
          body = await response.json();
        } catch {
          // No JSON body: read below as an answer without a token.
        }
        const answer = embeddedBootstrapResponseSchema.safeParse(body);
        setEmbeddedSessionCredential(answer.success ? answer.data : {});
        setPageReachable(embeddedCrmOrigin() !== null);
        bootstrapTokenRef.current = null;
      })();
    }

    await bootstrapExchangeRef.current;
  }, []);

  // Fetch organization ID and user role from validated session.
  // Exposed as refreshSession so consent grant can re-read the session AFTER the
  // backend assigns the user's org role — otherwise `role` stays at its stale
  // pre-consent value (null) and the org owner is wrongly gated behind
  // "Request Access". See EmbeddedConsentGate.handleGrantConsent.
  const refreshSession = useCallback(
    async (options?: RefreshSessionOptions): Promise<SessionRole> => {
      const quiet = options?.quiet === true;
      const mine = ++sessionReadSeq.current;
      async function fetchSessionData(): Promise<SessionRole> {
        try {
          if (!quiet) {
            setIsLoading(true);
            setError(null);
          }

          const { API_BASE_URL } = await import('@/lib/api/config');
          await ensureSessionCookie();
          // A bearer host's frame without its token in memory (reloaded, or
          // its exchange failed) must not fall back to the cookie: the
          // ui-worker origin may hold another CRM's session cookie, and the
          // frame would show that session inside this CRM's page (spec
          // 2026-10-24-uspacy-embedded-tab.md §3.5).
          if (frameUsesHostMessages() && !embeddedSessionAuthHeaders().Authorization) {
            throw new Error(
              `Session expired or invalid. Please reopen via ${embeddedHostDisplayName(embeddedHostHint())}.`
            );
          }

          const response = await fetch(`${API_BASE_URL}/api/v1/embedded/session`, {
            method: 'GET',
            credentials: 'include',
            headers: embeddedSessionAuthHeaders(),
          });

          if (!response.ok) {
            if (response.status === 404) {
              throw new Error(
                `Session expired or invalid. Please reopen via ${embeddedHostDisplayName(embeddedHostHint())}.`
              );
            }
            // An idle Bitrix24 tab comes back with no session cookie at all, and
            // this request is the first to find out — it runs above the gates,
            // before any widget exists to trip the recovery in `fetch-utils`.
            // Left alone it used to strand the tab: the gates hold a spinner on a
            // session that will never load, so the layout below never mounts and
            // never registers the recovery that would have repaired it.
            //
            // Awaiting the outcome matters. Claiming the 401 is synchronous, but
            // the frame can still refuse to mint credentials; only a claim that
            // became a real navigation may be swallowed. Anything else falls
            // through to the error below, where the user can at least see it.
            if (response.status === 401 && notifyEmbeddedSessionExpired()) {
              if (await awaitEmbeddedRecoveryOutcome()) {
                logWarn('Embedded session lapsed — re-entering through Bitrix24', {
                  component: 'EmbeddedOrgContext',
                  embeddedRecoveryHandled: true,
                });
                // Deliberately no setError: the page is navigating away, and the
                // loading state it leaves behind is the honest one.
                return;
              }
            }
            throw new Error(`HTTP ${response.status}`);
          }

          const parsed = embeddedSessionResponseSchema.safeParse(await response.json());
          if (!parsed.success) {
            throw new Error('Invalid embedded session response');
          }
          const data = parsed.data;

          // Role is now included in session response
          const userRole = data.role ? (data.role as GlobalRole) : null;

          // A read that started before one already committed answers for an
          // older moment: nothing is written, and its caller gets the role the
          // context holds now — not the stale one it read.
          if (mine <= sessionCommittedSeq.current) {
            return sessionCommittedRole.current;
          }
          sessionCommittedSeq.current = mine;
          sessionCommittedRole.current = userRole;
          setOrgId(data.orgId);
          setDomain(data.crmDomain);
          setCrmVendor(data.crmType);
          setRole(userRole);
          setUserEmail(data.userEmail);
          setCsrfToken(data.csrfToken);
          // A quiet re-read whose flag read failed — a member's answer without
          // `gates` — keeps the access in hand: a background re-read must not
          // close what is open, as the web flag map keeps its answer over a
          // failed refetch. Only the same organization's: the session cookie
          // is shared across portal tabs, and another tenant's access is not
          // held over, it fails closed.
          const flagsUnread = !data.pendingConsent && data.gates === undefined;
          setAccess((held) =>
            quiet && flagsUnread && held?.orgId === data.orgId
              ? held
              : {
                  orgId: data.orgId,
                  features: data.features,
                  flags: data.flags,
                  gates: data.gates,
                  screenFlagsOff: data.screenFlagsOff,
                  proTrial: data.proTrial,
                }
          );
          setConsentRequired(data.consentRequired);
          setPendingConsent(data.pendingConsent);
          setOnboardingSurveyCompleted(data.onboardingSurveyCompleted);
          setComplianceConfirmed(data.complianceConfirmed);
          setWeekStartsOn(isWeekStartsOn(data.weekStartsOn) ? data.weekStartsOn : null);

          logInfo('Embedded session validated', {
            component: 'EmbeddedOrgContext',
            orgId: data.orgId,
            domain: data.crmDomain,
            role: userRole,
            expiresAt: data.expiresAt,
          });
          return userRole;
        } catch (err) {
          logError(
            'Failed to validate embedded session',
            {
              component: 'EmbeddedOrgContext',
            },
            err instanceof Error ? err : undefined
          );
          if (!quiet) {
            setError(err instanceof Error ? err.message : 'Failed to validate session');
          }
          return undefined;
        } finally {
          if (!quiet) setIsLoading(false);
        }
      }

      return fetchSessionData();
    },
    [ensureSessionCookie]
  );

  useEffect(() => {
    void refreshSession();
  }, [refreshSession]);

  // A message host (Uspacy) gets the frame's height as `aelo:height`. It is
  // told from here, above the consent, access, compliance and onboarding
  // gates: the layout below them mounts only once they let the person through,
  // and the page's frame would stay at its 400 px for as long as a gate is on
  // screen. Started once the exchange has named the page (before it nothing
  // can be sent): the first value goes out at once, and the observer follows
  // every change of the body after it. A cookie session (Bitrix24) never
  // reaches the page this way and starts nothing.
  useEffect(() => {
    if (!pageReachable) return;
    return observeHostHeight();
  }, [pageReachable]);

  // A background re-read (the start and the end of a Pro trial, #2816): the
  // screen stays as it is while the session is asked again.
  const refreshSessionQuietly = useCallback(
    () => refreshSession({ quiet: true }),
    [refreshSession]
  );
  useTrialEndRefresh(access?.proTrial, refreshSessionQuietly);
  useTrialStartRefreshOnFocus(access?.proTrial, refreshSessionQuietly);

  const features = access?.features;
  const contextValue = useMemo(
    () => ({
      orgId,
      domain,
      crmVendor,
      role,
      userEmail,
      csrfToken,
      assistantEnabled: features?.assistant ?? false,
      voiceOfCustomerEnabled: features?.voiceOfCustomer ?? false,
      voiceOfCustomerUnchecked: features?.voiceOfCustomerUnchecked ?? false,
      consentRequired,
      pendingConsent,
      onboardingSurveyCompleted,
      complianceConfirmed,
      weekStartsOn,
      isLoading,
      error,
      clearConsentRequired,
      refreshSession,
    }),
    [
      orgId,
      domain,
      crmVendor,
      role,
      userEmail,
      csrfToken,
      features,
      consentRequired,
      pendingConsent,
      onboardingSurveyCompleted,
      complianceConfirmed,
      weekStartsOn,
      isLoading,
      error,
      clearConsentRequired,
      refreshSession,
    ]
  );

  return (
    <EmbeddedOrgContext.Provider value={contextValue}>
      {/* The embedded tree's flag map (#1347): the session's subset, read by
          `useFeatureFlagsSafe()` exactly as the web app's. */}
      <SessionFeatureFlagsProvider
        flags={access?.flags ?? null}
        gates={access?.gates}
        screenFlagsOff={access?.screenFlagsOff}
        proTrial={access?.proTrial}
        orgId={orgId}
        error={error}
      >
        {children}
      </SessionFeatureFlagsProvider>
    </EmbeddedOrgContext.Provider>
  );
}

export function useEmbeddedOrg() {
  const context = useContext(EmbeddedOrgContext);
  if (!context) {
    throw new Error('useEmbeddedOrg must be used within EmbeddedOrgProvider');
  }
  return context;
}

/**
 * Safe version of useEmbeddedOrg that returns null values if not in EmbeddedOrgProvider.
 * Use this in components that need to work in both embedded and regular modes.
 */
/** Inside the Bitrix24 embedded session (its provider is mounted), not the web app. */
export function useInEmbeddedSession(): boolean {
  return useContext(EmbeddedOrgContext) !== null;
}

export function useEmbeddedOrgSafe(): EmbeddedOrgContextValue {
  const context = useContext(EmbeddedOrgContext);
  if (!context) {
    // Return safe defaults for non-embedded mode
    return {
      orgId: null,
      domain: null,
      crmVendor: null,
      role: null,
      userEmail: null,
      csrfToken: null,
      assistantEnabled: false,
      voiceOfCustomerEnabled: false,
      voiceOfCustomerUnchecked: false,
      consentRequired: null,
      pendingConsent: false,
      onboardingSurveyCompleted: true,
      complianceConfirmed: true,
      weekStartsOn: null,
      isLoading: false,
      error: null,
      clearConsentRequired: () => {},
      refreshSession: () => Promise.resolve(null),
    };
  }
  return context;
}
